import { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Info } from "lucide-react";
import { PageMeta } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { ImageSlot } from "@/components/ImageSlot";
import { PageHero, SectionHeader } from "@/components/Section";
import { SEO, CONTACT, CONTACT_PAGE, IMAGE_SLOTS } from "@/content/siteContent";

const initialForm = {
    name: "",
    email: "",
    phone: "",
    organisation: "",
    reason: "",
    message: "",
    consent: false,
};

const validate = (f) => {
    const errors = {};
    if (!f.name.trim()) errors.name = "Please tell us your name.";
    if (!f.email.trim()) errors.email = "Please provide your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()))
        errors.email = "Please enter a valid email address.";
    if (!f.reason) errors.reason = "Please choose a reason for contacting us.";
    if (!f.message.trim()) errors.message = "Please write your message.";
    return errors;
};

export default function Contact() {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);

    const set = (key) => (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const errs = validate(form);
        setErrors(errs);
        if (Object.keys(errs).length === 0) setSubmitted(true);
    };

    return (
        <>
            <PageMeta title={SEO.contact.title} description={SEO.contact.description} />
            <PageHero eyebrow="Contact Us" title={CONTACT_PAGE.title} intro={CONTACT_PAGE.intro} />

            <section className="surface-dark py-20 sm:py-28" data-testid="contact-section">
                <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-[1fr_1.25fr]">
                    {/* Official details */}
                    <div>
                        <SectionHeader eyebrow="Official Details" title={CONTACT_PAGE.subtitle} dark />
                        <Reveal delay={0.1}>
                            <ul className="mt-9 space-y-7" data-testid="contact-details">
                                <li className="flex items-start gap-4">
                                    <Mail size={18} className="mt-1 shrink-0 text-[#C8A15A]" />
                                    <div>
                                        <p className="text-[0.68rem] uppercase tracking-[0.24em] text-[#FFF5E6]/50">Email</p>
                                        <a
                                            href={`mailto:${CONTACT.email}`}
                                            className="mt-1 block text-lg text-[#FFF5E6] transition-colors hover:text-[#C8A15A]"
                                            data-testid="contact-email-link"
                                        >
                                            {CONTACT.email}
                                        </a>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <Phone size={18} className="mt-1 shrink-0 text-[#C8A15A]" />
                                    <div>
                                        <p className="text-[0.68rem] uppercase tracking-[0.24em] text-[#FFF5E6]/50">Phone / WhatsApp</p>
                                        <a
                                            href={CONTACT.phoneHref}
                                            className="mt-1 block text-lg text-[#FFF5E6] transition-colors hover:text-[#C8A15A]"
                                            data-testid="contact-phone-link"
                                        >
                                            {CONTACT.phoneDisplay}
                                        </a>
                                        <a
                                            href={CONTACT.whatsappHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-[#C8A15A] transition-colors hover:text-[#e2c07f]"
                                            data-testid="contact-whatsapp-link"
                                        >
                                            <MessageCircle size={14} /> Message on WhatsApp
                                        </a>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <MapPin size={18} className="mt-1 shrink-0 text-[#C8A15A]" />
                                    <div>
                                        <p className="text-[0.68rem] uppercase tracking-[0.24em] text-[#FFF5E6]/50">Address</p>
                                        <p className="mt-1 text-lg leading-relaxed text-[#FFF5E6]" data-testid="contact-address">
                                            {CONTACT.address}
                                            <br />
                                            <span className="text-base text-[#FFF5E6]/65">{CONTACT.locality}</span>
                                        </p>
                                    </div>
                                </li>
                            </ul>
                        </Reveal>

                        {/* The sanctum — added when the photograph is supplied (Phase 2) */}
                        {IMAGE_SLOTS.sanctum?.src && (
                            <Reveal delay={0.2} className="mt-14">
                                <ImageSlot
                                    slotKey="sanctum"
                                    caption="The sanctum at Chetla, where our Puja takes place."
                                />
                            </Reveal>
                        )}
                    </div>

                    {/* Enquiry form — preview, not connected */}
                    <Reveal delay={0.15}>
                        <div className="rounded-sm border border-[#C8A15A]/20 bg-[#160a0e] p-7 sm:p-10">
                            <h2 className="font-display text-2xl font-semibold text-[#FFF5E6] sm:text-3xl">
                                Send an enquiry
                            </h2>

                            {submitted ? (
                                <div
                                    className="mt-7 rounded-sm border border-[#C8A15A]/35 bg-[#C8A15A]/8 p-6"
                                    role="status"
                                    data-testid="form-submitted-note"
                                >
                                    <p className="flex items-start gap-3 text-sm leading-relaxed text-[#FFF5E6]/85">
                                        <Info size={17} className="mt-0.5 shrink-0 text-[#C8A15A]" />
                                        Thank you for writing to us{form.name ? `, ${form.name.trim()}` : ""}.
                                        To be sure your enquiry reaches the committee while our online
                                        submission is being connected, please also send it to{" "}
                                        <a
                                            href={`mailto:${CONTACT.email}`}
                                            className="text-[#C8A15A] underline-offset-4 hover:underline"
                                        >
                                            {CONTACT.email}
                                        </a>
                                        . The committee will respond as soon as practicable.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => { setSubmitted(false); setForm(initialForm); }}
                                        className="mt-4 block text-sm text-[#C8A15A] underline-offset-4 hover:underline"
                                        data-testid="form-preview-reset"
                                    >
                                        Fill the form again
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} noValidate className="mt-8 space-y-6" data-testid="contact-form">
                                    <div className="grid gap-6 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="cf-name" className="mb-2 block text-[0.7rem] uppercase tracking-[0.2em] text-[#FFF5E6]/60">
                                                Name <span className="text-[#C8A15A]">*</span>
                                            </label>
                                            <input
                                                id="cf-name" type="text" className={`field ${errors.name ? "field-error" : ""}`}
                                                value={form.name} onChange={set("name")}
                                                data-testid="form-name-input" autoComplete="name"
                                            />
                                            {errors.name && <p className="mt-2 text-xs text-[#e0808f]" data-testid="form-name-error">{errors.name}</p>}
                                        </div>
                                        <div>
                                            <label htmlFor="cf-email" className="mb-2 block text-[0.7rem] uppercase tracking-[0.2em] text-[#FFF5E6]/60">
                                                Email address <span className="text-[#C8A15A]">*</span>
                                            </label>
                                            <input
                                                id="cf-email" type="email" className={`field ${errors.email ? "field-error" : ""}`}
                                                value={form.email} onChange={set("email")}
                                                data-testid="form-email-input" autoComplete="email"
                                            />
                                            {errors.email && <p className="mt-2 text-xs text-[#e0808f]" data-testid="form-email-error">{errors.email}</p>}
                                        </div>
                                    </div>
                                    <div className="grid gap-6 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="cf-phone" className="mb-2 block text-[0.7rem] uppercase tracking-[0.2em] text-[#FFF5E6]/60">
                                                Phone number <span className="normal-case tracking-normal text-[#FFF5E6]/40">(optional)</span>
                                            </label>
                                            <input
                                                id="cf-phone" type="tel" className="field"
                                                value={form.phone} onChange={set("phone")}
                                                data-testid="form-phone-input" autoComplete="tel"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="cf-org" className="mb-2 block text-[0.7rem] uppercase tracking-[0.2em] text-[#FFF5E6]/60">
                                                Organisation / business <span className="normal-case tracking-normal text-[#FFF5E6]/40">(optional)</span>
                                            </label>
                                            <input
                                                id="cf-org" type="text" className="field"
                                                value={form.organisation} onChange={set("organisation")}
                                                data-testid="form-org-input" autoComplete="organization"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="cf-reason" className="mb-2 block text-[0.7rem] uppercase tracking-[0.2em] text-[#FFF5E6]/60">
                                            Reason for contacting us <span className="text-[#C8A15A]">*</span>
                                        </label>
                                        <select
                                            id="cf-reason" className={`field ${errors.reason ? "field-error" : ""}`}
                                            value={form.reason} onChange={set("reason")}
                                            data-testid="form-reason-select"
                                        >
                                            <option value="">Select one</option>
                                            {CONTACT_PAGE.reasons.map((r) => (
                                                <option key={r} value={r}>{r}</option>
                                            ))}
                                        </select>
                                        {errors.reason && <p className="mt-2 text-xs text-[#e0808f]" data-testid="form-reason-error">{errors.reason}</p>}
                                    </div>
                                    <div>
                                        <label htmlFor="cf-message" className="mb-2 block text-[0.7rem] uppercase tracking-[0.2em] text-[#FFF5E6]/60">
                                            Message <span className="text-[#C8A15A]">*</span>
                                        </label>
                                        <textarea
                                            id="cf-message" rows={5} className={`field resize-y ${errors.message ? "field-error" : ""}`}
                                            value={form.message} onChange={set("message")}
                                            data-testid="form-message-input"
                                        />
                                        {errors.message && <p className="mt-2 text-xs text-[#e0808f]" data-testid="form-message-error">{errors.message}</p>}
                                    </div>
                                    <label className="flex cursor-pointer items-start gap-3" data-testid="form-consent-label">
                                        <input
                                            type="checkbox"
                                            checked={form.consent}
                                            onChange={set("consent")}
                                            className="mt-1 h-4 w-4 shrink-0 accent-[#C8A15A]"
                                            data-testid="form-consent-checkbox"
                                        />
                                        <span className="text-xs font-light leading-relaxed text-[#FFF5E6]/65">
                                            {CONTACT_PAGE.consentLabel}
                                        </span>
                                    </label>
                                    <button type="submit" className="btn btn-gold" data-testid="form-submit-button">
                                        Send Enquiry
                                    </button>
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
